import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { MessageCircle, SkipForward, X, CheckCircle2, Loader2 } from "lucide-react";
import { buildWaUrl } from "@/lib/messageEngine";

/**
 * CampaignRunner — Guided one-at-a-time WhatsApp send flow.
 * Key constraint from the user: open ONE wa.me tab at a time, then mark
 * this recipient sent/skipped before moving on. Keeps the user honest and
 * avoids getting flagged as spam by WhatsApp.
 */
export default function CampaignRunner({ campaignId, open, onClose, onProgress }) {
  const [campaign, setCampaign] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${campaignId}`);
      setCampaign(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load campaign");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) { setError(""); setCampaign(null); setLoading(true); load(); } }, [open, campaignId]);

  if (!open) return null;

  const recipients = campaign?.recipients || [];
  const current = recipients.find((r) => r.status === "pending");
  const done = recipients.filter((r) => r.status !== "pending").length;
  const total = recipients.length || 1;
  const pct = Math.round((done / total) * 100);

  const mark = async (status) => {
    if (!current) return;
    setBusy(true);
    try {
      await api.post(`/campaigns/${campaignId}/mark-sent`, {
        contact_id: current.contact_id,
        status,
      });
      await load();
      onProgress?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark sent");
    } finally {
      setBusy(false);
    }
  };

  const openWaAndMark = () => {
    if (!current) return;
    const waUrl = buildWaUrl(current.phone, current.body);
    if (!waUrl) {
      toast.error("This contact has no valid phone number");
      mark("skipped");
      return;
    }
    window.open(waUrl, "_blank", "noopener,noreferrer");
    // Short delay to give the user a beat to see the tab opened before
    // we flip the row to "sent" — they can still undo by closing this dialog.
    setTimeout(() => mark("sent"), 400);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[560px] rounded-3xl bg-background shadow-2xl" data-testid="campaign-runner">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {campaign?.name || "Campaign"}
          </DialogTitle>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
              {campaign?.channel || "whatsapp"}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {done} of {total} processed
            </span>
          </div>
          <Progress value={pct} className="mt-2 h-2" data-testid="runner-progress" />
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {loading ? (
          <div className="py-12 text-center space-y-2" data-testid="runner-loading">
            <Loader2 className="h-8 w-8 text-primary mx-auto animate-spin" />
            <p className="text-sm text-muted-foreground">Loading recipients…</p>
          </div>
        ) : !current ? (
          <div className="py-10 text-center space-y-2" data-testid="runner-done">
            <CheckCircle2 className="h-10 w-10 text-primary mx-auto" />
            <p className="font-display font-black text-xl">All done.</p>
            <p className="text-sm text-muted-foreground">
              {campaign?.sent_count || 0} sent · {campaign?.skipped_count || 0} skipped · {campaign?.failed_count || 0} failed
            </p>
            <Button onClick={onClose} className="mt-3">Close</Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="runner-current">
            <div className="p-4 rounded-2xl bg-muted/40 border border-border/60">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Up next</p>
              <p className="font-semibold mt-1 truncate">{current.name || "—"}</p>
              <p className="text-xs text-muted-foreground truncate">
                {current.company_name ? `${current.company_name} · ` : ""}{current.phone || "no phone"}
              </p>
            </div>
            <div className="p-3 rounded-2xl border border-border/60 bg-card max-h-64 overflow-y-auto">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Message preview</p>
              <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">{current.body}</pre>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={openWaAndMark}
                disabled={busy}
                className="flex-1"
                data-testid="runner-send"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <MessageCircle className="h-4 w-4 mr-1.5" />}
                Open WhatsApp & mark sent
              </Button>
              <Button
                variant="outline"
                onClick={() => mark("skipped")}
                disabled={busy}
                data-testid="runner-skip"
              >
                <SkipForward className="h-4 w-4 mr-1.5" /> Skip
              </Button>
              <Button
                variant="ghost"
                onClick={() => mark("failed")}
                disabled={busy}
                className="text-red-600 hover:text-red-700"
                data-testid="runner-fail"
              >
                <X className="h-4 w-4 mr-1.5" /> Mark failed
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground text-center">
              One tab opens at a time. Send manually in WhatsApp, then we'll auto-queue the next recipient.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
